import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const slides = [
  { city: "Dubai, UAE", image: "https://public.readdy.ai/ai/img_res/c41e9c9c219ac16f432881a24def430d.jpg" },
  { city: "Riyadh, Saudi Arabia", image: "https://readdy.ai/api/search-image?query=Riyadh%20Saudi%20Arabia%20Kingdom%20Tower%20golden%20sunset%20modern%20skyline%20panoramic%20aerial%20view%20warm%20amber%20glow%20cinematic%20photography%20stunning%20cityscape&width=1920&height=1080&seq=slide2&orientation=landscape" },
  { city: "Doha, Qatar", image: "https://readdy.ai/api/search-image?query=Doha%20Qatar%20West%20Bay%20skyline%20golden%20hour%20waterfront%20modern%20towers%20reflection%20sea%20cinematic%20aerial%20panoramic%20photography%20stunning%20warm%20light&width=1920&height=1080&seq=slide3&orientation=landscape" },
  { city: "Abu Dhabi, UAE", image: "https://readdy.ai/api/search-image?query=Abu%20Dhabi%20UAE%20Sheikh%20Zayed%20Grand%20Mosque%20golden%20sunset%20aerial%20view%20luxury%20architecture%20warm%20amber%20light%20cinematic%20panoramic%20photography%20stunning&width=1920&height=1080&seq=slide4&orientation=landscape" },
  { city: "Muscat, Oman", image: "https://readdy.ai/api/search-image?query=Muscat%20Oman%20modern%20cityscape%20golden%20hour%20mountains%20sea%20harbor%20aerial%20view%20warm%20amber%20cinematic%20panoramic%20photography%20stunning%20architecture&width=1920&height=1080&seq=slide5&orientation=landscape" },
  { city: "Kuwait City, Kuwait", image: "https://readdy.ai/api/search-image?query=Kuwait%20City%20skyline%20golden%20sunset%20Liberation%20Tower%20modern%20architecture%20aerial%20view%20warm%20amber%20light%20cinematic%20panoramic%20photography%20stunning&width=1920&height=1080&seq=slide6&orientation=landscape" },
  { city: "Manama, Bahrain", image: "https://readdy.ai/api/search-image?query=Manama%20Bahrain%20skyline%20golden%20hour%20modern%20towers%20sea%20bridge%20aerial%20view%20warm%20amber%20cinematic%20panoramic%20photography%20stunning%20cityscape&width=1920&height=1080&seq=slide7&orientation=landscape" },
  { city: "Istanbul, Turkey", image: "https://readdy.ai/api/search-image?query=Istanbul%20Turkey%20Bosphorus%20bridge%20golden%20sunset%20aerial%20view%20historic%20modern%20skyline%20warm%20amber%20cinematic%20panoramic%20photography%20stunning&width=1920&height=1080&seq=slide8&orientation=landscape" },
  { city: "Jeddah, Saudi Arabia", image: "https://readdy.ai/api/search-image?query=Jeddah%20Saudi%20Arabia%20corniche%20waterfront%20golden%20hour%20modern%20skyline%20King%20Fahd%20Fountain%20aerial%20view%20warm%20amber%20cinematic%20panoramic%20photography&width=1920&height=1080&seq=slide9&orientation=landscape" },
  { city: "Sharjah, UAE", image: "https://readdy.ai/api/search-image?query=Sharjah%20UAE%20golden%20hour%20waterfront%20lagoon%20modern%20architecture%20aerial%20view%20warm%20amber%20cinematic%20panoramic%20photography%20stunning%20cityscape&width=1920&height=1080&seq=slide10&orientation=landscape" },
];

const HeroSection = () => {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [category, setCategory] = useState("");
  const [country, setCountry] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();

  const goTo = useCallback((idx: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 600);
  }, [animating]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (keyword) params.set("q", keyword);
    if (category) params.set("category", category);
    if (country) params.set("country", country);
    navigate(`/products?${params.toString()}`);
  };

  const quickLinks = [
    { labelKey: "hero.dropshippingProducts", icon: "ri-truck-line", path: "/products?dropshipping=true" },
    { labelKey: "hero.verifiedSuppliers", icon: "ri-shield-check-line", path: "/products?verified=true" },
    { labelKey: "hero.registerSupplier", icon: "ri-building-4-line", path: "/register-supplier" },
    { labelKey: "hero.influencerCatalog", icon: "ri-star-line", path: "/products" },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {slides.map((slide, i) => (
        <div
          key={slide.city}
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: i === current ? 1 : 0, zIndex: i === current ? 1 : 0 }}
        >
          <img src={slide.image} alt={slide.city} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-amber-900/70 via-amber-800/55 to-amber-950/80"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-900/40 via-transparent to-yellow-900/30"></div>
        </div>
      ))}

      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 z-10"></div>

      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-4 md:px-8 pt-28 pb-20 text-center">
        <div className="inline-flex items-center gap-2 bg-amber-400/20 border border-amber-300/50 text-amber-200 text-xs font-semibold px-5 py-2 rounded-full mb-8 backdrop-blur-sm">
          <i className="ri-verified-badge-fill text-amber-300"></i>
          {t("hero.badge")}
        </div>

        <h1
          className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight"
          style={{ fontFamily: "'Playfair Display', serif", textShadow: "0 4px 32px rgba(0,0,0,0.5)" }}
        >
          {t("hero.headline1")}
          <br />
          <span className="text-amber-300">{t("hero.headline2")}</span>
          <br />
          {t("hero.headline3")}
        </h1>

        <p
          className="text-xl md:text-3xl lg:text-4xl font-semibold text-amber-100 mb-5 max-w-5xl mx-auto leading-snug"
          style={{ textShadow: "0 2px 16px rgba(0,0,0,0.4)" }}
        >
          {t("hero.subheadline").replace("SuperMiddleEast", "")}
          <span className="text-amber-300 font-bold">SuperMiddleEast</span>
          {" "}{t("hero.subheadline").includes("SuperMiddleEast") ? "" : t("hero.subheadline")}
        </p>

        <p
          className="text-base md:text-xl text-amber-200 mb-10 max-w-3xl mx-auto leading-relaxed"
          style={{ textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}
        >
          {t("hero.tagline")}
          <br className="hidden md:block" />
          <span className="font-semibold text-white"> {t("hero.tagline2")}</span>
        </p>

        <form onSubmit={handleSearch} className="w-full max-w-4xl mx-auto mb-8">
          <div className="flex flex-col sm:flex-row bg-white rounded-xl overflow-hidden shadow-2xl border-2 border-amber-300/40">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={t("hero.searchPlaceholder")}
              className="flex-1 px-5 py-4 text-gray-800 text-sm focus:outline-none"
            />
            <div className="hidden sm:block w-px bg-gray-200 my-3"></div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-4 py-4 text-gray-600 text-sm focus:outline-none bg-white border-t sm:border-t-0 border-gray-100 cursor-pointer"
            >
              <option value="">{t("hero.allCategories")}</option>
              <option value="electronics">{t("hero.electronics")}</option>
              <option value="textiles">{t("hero.textiles")}</option>
              <option value="machinery">{t("hero.machinery")}</option>
              <option value="food">{t("hero.food")}</option>
              <option value="furniture">{t("hero.furniture")}</option>
              <option value="beauty">{t("hero.beauty")}</option>
            </select>
            <div className="hidden sm:block w-px bg-gray-200 my-3"></div>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="px-4 py-4 text-gray-600 text-sm focus:outline-none bg-white border-t sm:border-t-0 border-gray-100 cursor-pointer"
            >
              <option value="">{t("hero.allCountries")}</option>
              <option value="uae">UAE / Dubai</option>
              <option value="saudi-arabia">Saudi Arabia</option>
              <option value="qatar">Qatar</option>
              <option value="turkey">Turkey</option>
            </select>
            <button
              type="submit"
              className="bg-amber-500 hover:bg-amber-400 text-white px-8 py-4 font-bold text-sm transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 justify-center"
            >
              <i className="ri-search-line"></i>
              {t("hero.search")}
            </button>
          </div>
        </form>

        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {quickLinks.map((link) => (
            <a
              key={link.labelKey}
              href={link.path}
              className="flex items-center gap-2 bg-amber-500/20 hover:bg-amber-400/40 border border-amber-300/40 text-white text-sm px-5 py-2.5 rounded-full backdrop-blur-sm transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <i className={`${link.icon} text-amber-300`}></i>
              {t(link.labelKey)}
            </a>
          ))}
        </div>

        <div className="flex items-center justify-center gap-2 text-amber-300/80 text-sm font-medium">
          <i className="ri-map-pin-line text-amber-400"></i>
          <span key={current} className="transition-all duration-500" style={{ animation: "fadeInUp 0.5s ease" }}>
            {slides[current].city}
          </span>
        </div>
      </div>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${i === current ? "w-8 h-2 bg-amber-400" : "w-2 h-2 bg-white/40 hover:bg-amber-300/60"}`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + slides.length) % slides.length)}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-amber-500/30 hover:bg-amber-500/60 border border-amber-300/40 text-white rounded-full backdrop-blur-sm transition-all cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={() => goTo((current + 1) % slides.length)}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-amber-500/30 hover:bg-amber-500/60 border border-amber-300/40 text-white rounded-full backdrop-blur-sm transition-all cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
